/*
  File: assets/ozplug-product-card.js
  Purpose: Global AJAX Add + handler for OZPlug product cards + Select Options popup for multi-variant products.
*/
(function () {
  if (window.OZPlugProductCardGlobalInit) return;
  window.OZPlugProductCardGlobalInit = true;

  const FORM_SELECTOR = "[data-oz-card-form]";
  const BUTTON_SELECTOR = "[data-oz-card-button]";
  const LABEL_SELECTOR = "[data-oz-card-button-label]";
  const ERROR_SELECTOR = "[data-oz-card-error]";
  const OPTIONS_TRIGGER_SELECTOR = "[data-oz-card-options-trigger]";
  const PRODUCT_JSON_SELECTOR = "[data-oz-card-product-json]";
  const VIDEO_SELECTOR = "[data-oz-card-video]";
  const VIDEO_CARD_SELECTOR = "[data-oz-product-card]";
  const VIDEO_FULL_THRESHOLD = 0.62;
  const VIDEO_STOP_THRESHOLD = 0.12;
  const VIDEO_PLAY_DELAY = 120;
  const VIDEO_ROOT_MARGIN = "220px 0px";

  let videoObserver = null;
  const observedVideoCards = new WeakSet();
  const videoTimers = new WeakMap();
  let videoInitFrame = null;

  let activeProduct = null;
  let activeSelectedOptions = [];
  let activeQuantity = 1;
  let activeTrigger = null;

  const productDataCache = new Map();

  function closestFromEvent(event, selector) {
    const target = event && event.target;
    return target && typeof target.closest === "function" ? target.closest(selector) : null;
  }

  document.addEventListener("submit", function (event) {
    const form = closestFromEvent(event, FORM_SELECTOR);
    if (!form) return;

    event.preventDefault();
    addProduct(form);
  });

  document.addEventListener("click", async function (event) {
    const trigger = closestFromEvent(event, OPTIONS_TRIGGER_SELECTOR);
    if (!trigger) return;

    const card = trigger.closest("[data-oz-product-card]");
    const jsonScript = card ? card.querySelector(PRODUCT_JSON_SELECTOR) : null;
    if (!jsonScript) return;

    event.preventDefault();

    try {
      trigger.setAttribute("aria-busy", "true");
      trigger.classList.add("is-loading");

      const payload = parseCardProductPayload(jsonScript);
      const product = await resolveCardProductData(payload);
      openOptionsModal(product, trigger);
    } catch (error) {
      window.location.href = trigger.getAttribute("href") || "/collections/all";
    } finally {
      trigger.removeAttribute("aria-busy");
      trigger.classList.remove("is-loading");
    }
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") closeOptionsModal();
  });

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) pauseAllCardVideos();
  });

  document.addEventListener("shopify:section:load", function () {
    scheduleViewportVideoInit();
  });

  document.addEventListener("ozplug:collection-page-loaded", function () {
    scheduleViewportVideoInit();
  });

  ready(scheduleViewportVideoInit);

  async function addProduct(form) {
    const button = form.querySelector(BUTTON_SELECTOR);
    const label = form.querySelector(LABEL_SELECTOR);
    const card = form.closest("[data-oz-product-card]");
    const errorElement = card ? card.querySelector(ERROR_SELECTOR) : null;

    if (!button || button.disabled) return;

    const originalLabel = label ? label.textContent : button.textContent;
    const addingLabel = form.dataset.addingLabel || "Adding…";
    const addedLabel = form.dataset.addedLabel || "Added ✓";
    const errorMessage = form.dataset.errorMessage || "This product could not be added to the cart.";

    setError(errorElement, "");
    setButtonLabel(button, label, addingLabel);
    button.disabled = true;
    button.classList.add("is-loading");

    try {
      const result = await addFormDataToCart(new FormData(form), form.dataset.cartAddUrl || "/cart/add.js", form.dataset.cartUrl || "/cart.js");

      button.classList.remove("is-loading");
      button.classList.add("is-added");
      setButtonLabel(button, label, addedLabel);

      showToast(form.dataset.successMessage || "Added to cart");
      dispatchCartEvents(result.item, result.cart, form.dataset.sectionId || "global-product-card");

      window.setTimeout(function () {
        button.disabled = false;
        button.classList.remove("is-added");
        setButtonLabel(button, label, originalLabel);
      }, 1300);
    } catch (error) {
      button.disabled = false;
      button.classList.remove("is-loading", "is-added");
      setButtonLabel(button, label, originalLabel);
      setError(errorElement, error instanceof Error ? error.message : errorMessage);
    }
  }

  function parseCardProductPayload(jsonScript) {
    try {
      return JSON.parse(jsonScript.textContent || "{}");
    } catch (error) {
      return {};
    }
  }

  async function resolveCardProductData(payload) {
    if (payload && Array.isArray(payload.variants) && payload.variants.length) {
      return payload;
    }

    const productJsonUrl = payload && payload.productJsonUrl;
    if (!productJsonUrl) throw new Error("Missing product data URL.");

    if (productDataCache.has(productJsonUrl)) {
      return productDataCache.get(productJsonUrl);
    }

    const response = await fetchWithTimeout(productJsonUrl, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    }, 8000);

    if (!response.ok) throw new Error("Product options could not be loaded.");

    const rawProduct = await response.json();
    const product = normalizeShopifyProductJson(rawProduct, payload);
    productDataCache.set(productJsonUrl, product);
    return product;
  }

  async function fetchWithTimeout(url, options, timeoutMs) {
    if (!("AbortController" in window)) {
      return fetch(url, options);
    }

    const controller = new AbortController();
    const timer = window.setTimeout(function () {
      controller.abort();
    }, timeoutMs || 8000);

    try {
      return await fetch(url, Object.assign({}, options || {}, { signal: controller.signal }));
    } finally {
      window.clearTimeout(timer);
    }
  }

  function normalizeShopifyProductJson(rawProduct, payload) {
    const optionNames = normalizeOptionNames(rawProduct.options);
    const currencyCode = payload.currencyCode || getActiveCurrencyCode();
    const rawVariants = Array.isArray(rawProduct.variants) ? rawProduct.variants : [];

    const variants = rawVariants.map(function (variant) {
      const options = optionNames.map(function (_name, index) {
        return variant["option" + (index + 1)] || (Array.isArray(variant.options) ? variant.options[index] : "");
      });

      const price = Number(variant.price || 0);
      const compareAtPrice = Number(variant.compare_at_price || 0);

      return {
        id: variant.id,
        available: Boolean(variant.available),
        price_html: formatMoneyWithoutTrailingZeros(price, currencyCode),
        compare_html: compareAtPrice > price ? formatMoneyWithoutTrailingZeros(compareAtPrice, currencyCode) : null,
        options: options
      };
    });

    return {
      cartAddUrl: payload.cartAddUrl || "/cart/add.js",
      cartUrl: payload.cartUrl || "/cart.js",
      addingLabel: payload.addingLabel || "Adding…",
      addedLabel: payload.addedLabel || "Added ✓",
      successMessage: payload.successMessage || "Added to cart",
      errorMessage: payload.errorMessage || "This product could not be added to the cart.",
      productUrl: payload.productUrl || rawProduct.url || "/collections/all",
      options: optionNames.map(function (name, optionIndex) {
        return {
          name: name,
          values: unique(variants.map(function (variant) { return variant.options[optionIndex]; }).filter(Boolean))
        };
      }),
      variants: variants
    };
  }

  function normalizeOptionNames(options) {
    if (!Array.isArray(options)) return [];

    return options.map(function (option, index) {
      if (typeof option === "string") return option;
      return option && option.name ? option.name : "Option " + (index + 1);
    }).filter(Boolean);
  }

  function unique(values) {
    return values.filter(function (value, index, array) {
      return array.indexOf(value) === index;
    });
  }

  function getActiveCurrencyCode() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }

    return "USD";
  }

  function formatMoneyWithoutTrailingZeros(cents, currencyCode) {
    const amount = Number(cents || 0) / 100;
    const hasDecimals = Math.round(amount) !== amount;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: currencyCode || "USD",
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return "$" + amount.toFixed(hasDecimals ? 2 : 0);
    }
  }

  function openOptionsModal(product, trigger) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) {
      window.location.href = trigger.getAttribute("href") || "/collections/all";
      return;
    }

    activeProduct = product;
    activeTrigger = trigger;
    activeQuantity = 1;

    const firstAvailable = product.variants.find(function (variant) { return variant.available; }) || product.variants[0];
    activeSelectedOptions = Array.isArray(firstAvailable.options) ? firstAvailable.options.slice() : [];

    const modal = getOptionsModal();
    renderOptionsModal();

    modal.classList.add("is-visible");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("oz-card-modal-open");

    const firstOption = modal.querySelector("[data-oz-modal-option]");
    const closeButton = modal.querySelector("[data-oz-modal-close]");
    window.setTimeout(function () {
      (firstOption || closeButton || modal).focus();
    }, 30);
  }

  function closeOptionsModal() {
    const modal = document.querySelector("[data-oz-card-options-modal]");
    if (!modal || !modal.classList.contains("is-visible")) return;

    modal.classList.remove("is-visible");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("oz-card-modal-open");

    if (activeTrigger && typeof activeTrigger.focus === "function") {
      activeTrigger.focus({ preventScroll: true });
    }

    activeProduct = null;
    activeSelectedOptions = [];
    activeQuantity = 1;
    activeTrigger = null;
  }

  function getOptionsModal() {
    let modal = document.querySelector("[data-oz-card-options-modal]");

    if (!modal) {
      modal = document.createElement("div");
      modal.className = "oz-card-options-modal";
      modal.setAttribute("data-oz-card-options-modal", "");
      modal.setAttribute("aria-hidden", "true");
      modal.innerHTML = '' +
        '<div class="oz-card-options-modal__overlay" data-oz-modal-close></div>' +
        '<div class="oz-card-options-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="oz-card-options-title">' +
          '<button class="oz-card-options-modal__close" type="button" aria-label="Close options" data-oz-modal-close>×</button>' +
          '<div class="oz-card-options-modal__content" data-oz-modal-content></div>' +
        '</div>';
      document.body.appendChild(modal);

      modal.addEventListener("click", function (event) {
        const clickedBackdrop = event.target === modal || event.target.classList.contains("oz-card-options-modal__overlay");
        const clickedClose = closestFromEvent(event, "[data-oz-modal-close]");

        if (clickedBackdrop || clickedClose) {
          event.preventDefault();
          closeOptionsModal();
          return;
        }

        const optionButton = closestFromEvent(event, "[data-oz-modal-option]");
        if (optionButton) {
          event.preventDefault();
          const optionIndex = parseInt(optionButton.dataset.optionIndex || "0", 10);
          activeSelectedOptions[optionIndex] = optionButton.dataset.optionValue || "";
          renderOptionsModal();
          return;
        }

        const quantityButton = closestFromEvent(event, "[data-oz-modal-qty]");
        if (quantityButton) {
          event.preventDefault();
          const delta = parseInt(quantityButton.dataset.ozModalQty || "0", 10);
          activeQuantity = Math.max(1, Math.min(99, activeQuantity + delta));
          renderOptionsModal();
          return;
        }

        const addButton = closestFromEvent(event, "[data-oz-modal-add]");
        if (addButton) {
          event.preventDefault();
          addSelectedVariantFromModal(addButton);
        }
      });
    }

    return modal;
  }

  function renderOptionsModal() {
    const modal = getOptionsModal();
    const content = modal.querySelector("[data-oz-modal-content]");
    const product = activeProduct;
    if (!content || !product) return;

    const selectedVariant = getSelectedVariant(product);
    const optionGroupsHtml = renderOptionGroups(product, selectedVariant);
    const compareHtml = selectedVariant && selectedVariant.compare_html
      ? '<span class="oz-card-options-modal__was"><s>' + escapeHtml(selectedVariant.compare_html) + '</s></span>'
      : '';
    const addDisabled = !selectedVariant || !selectedVariant.available;
    const priceHtml = selectedVariant ? escapeHtml(selectedVariant.price_html || '') : '';
    const addLabel = addDisabled ? 'Unavailable' : 'Add to cart';

    content.innerHTML = '' +
      '<div class="oz-card-options-modal__info oz-card-options-modal__info--simple">' +
        '<h3 class="oz-card-options-modal__sr-title" id="oz-card-options-title">Select options</h3>' +
        optionGroupsHtml +
        '<div class="oz-card-options-modal__bottom">' +
          '<div class="oz-card-options-modal__pricebox">' +
            '<span class="oz-card-options-modal__price">' + priceHtml + '</span>' +
            compareHtml +
          '</div>' +
          '<div class="oz-card-options-modal__qty" aria-label="Quantity selector">' +
            '<button type="button" data-oz-modal-qty="-1" aria-label="Decrease quantity">−</button>' +
            '<span>' + activeQuantity + '</span>' +
            '<button type="button" data-oz-modal-qty="1" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<button class="oz-card-options-modal__add" type="button" data-oz-modal-add ' + (addDisabled ? 'disabled' : '') + '>' +
          '<span data-oz-modal-add-label>' + addLabel + '</span>' +
          (!addDisabled && priceHtml ? '<span class="oz-card-options-modal__add-price">' + priceHtml + '</span>' : '') +
        '</button>' +
        '<p class="oz-card-options-modal__error" data-oz-modal-error hidden></p>' +
      '</div>';
  }

  function renderOptionGroups(product, selectedVariant) {
    if (!Array.isArray(product.options) || !product.options.length) return '';

    return product.options.map(function (option, optionIndex) {
      const values = Array.isArray(option.values) ? option.values : [];
      const selectedValue = activeSelectedOptions[optionIndex] || (selectedVariant && selectedVariant.options ? selectedVariant.options[optionIndex] : '');
      const buttons = values.map(function (value) {
        const isActive = String(value) === String(selectedValue);
        const isPossible = variantExistsForChoice(product, optionIndex, value);
        const matchingVariant = product.variants.find(function (variant) {
          return Array.isArray(variant.options) && String(variant.options[optionIndex]) === String(value);
        });
        const miniPrice = product.options.length === 1 && matchingVariant ? '<span>' + escapeHtml(matchingVariant.price_html || '') + '</span>' : '';

        return '<button class="oz-card-options-modal__option ' + (isActive ? 'is-active' : '') + '" type="button" data-oz-modal-option data-option-index="' + optionIndex + '" data-option-value="' + escapeHtml(value) + '" ' + (!isPossible ? 'disabled' : '') + '>' +
          '<strong>' + escapeHtml(value) + '</strong>' + miniPrice +
        '</button>';
      }).join('');

      return '<div class="oz-card-options-modal__group">' +
        '<div class="oz-card-options-modal__label">' + escapeHtml(option.name || ('Option ' + (optionIndex + 1))) +
          (selectedValue ? '<span>' + escapeHtml(selectedValue) + '</span>' : '') +
        '</div>' +
        '<div class="oz-card-options-modal__options">' + buttons + '</div>' +
      '</div>';
    }).join('');
  }

  function variantExistsForChoice(product, optionIndex, value) {
    const nextSelection = activeSelectedOptions.slice();
    nextSelection[optionIndex] = value;

    return product.variants.some(function (variant) {
      if (!Array.isArray(variant.options)) return false;
      return nextSelection.every(function (selected, index) {
        return !selected || String(variant.options[index]) === String(selected);
      });
    });
  }

  function getSelectedVariant(product) {
    if (!product || !Array.isArray(product.variants)) return null;

    return product.variants.find(function (variant) {
      if (!Array.isArray(variant.options)) return false;
      return activeSelectedOptions.every(function (selected, index) {
        return String(variant.options[index]) === String(selected);
      });
    }) || null;
  }

  async function addSelectedVariantFromModal(button) {
    const product = activeProduct;
    const selectedVariant = getSelectedVariant(product);
    const modal = getOptionsModal();
    const label = modal.querySelector("[data-oz-modal-add-label]");
    const errorElement = modal.querySelector("[data-oz-modal-error]");

    if (!selectedVariant || !selectedVariant.available || !button || button.disabled) return;

    const originalLabel = label ? label.textContent : button.textContent;
    setError(errorElement, "");
    setButtonLabel(button, label, product.addingLabel || "Adding…");
    button.disabled = true;
    button.classList.add("is-loading");

    try {
      const formData = new FormData();
      formData.append("id", String(selectedVariant.id));
      formData.append("quantity", String(activeQuantity || 1));

      const result = await addFormDataToCart(formData, product.cartAddUrl || "/cart/add.js", product.cartUrl || "/cart.js");

      button.classList.remove("is-loading");
      button.classList.add("is-added");
      setButtonLabel(button, label, product.addedLabel || "Added ✓");
      showToast(product.successMessage || "Added to cart");
      dispatchCartEvents(result.item, result.cart, "global-product-card-options-popup");

      window.setTimeout(function () {
        closeOptionsModal();
      }, 600);
    } catch (error) {
      button.disabled = false;
      button.classList.remove("is-loading", "is-added");
      setButtonLabel(button, label, originalLabel);
      setError(errorElement, error instanceof Error ? error.message : (product.errorMessage || "This product could not be added to the cart."));
    }
  }

  async function addFormDataToCart(formData, cartAddUrl, cartUrl) {
    const response = await fetch(cartAddUrl || "/cart/add.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    });

    const item = await response.json();

    if (!response.ok) {
      throw new Error(item.description || item.message || "This product could not be added to the cart.");
    }

    const cart = await fetchCart(cartUrl || "/cart.js");
    updateCartCount(cart.item_count || 0);

    return { item: item, cart: cart };
  }

  async function fetchCart(url) {
    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) throw new Error("Cart refresh failed.");
    return response.json();
  }

  function dispatchCartEvents(item, cart, sectionId) {
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent("ozplug:cart-added", {
      detail: {
        item: item,
        cart: cart,
        sectionId: sectionId || "global-product-card"
      }
    }));
  }

  function updateCartCount(count) {
    document
      .querySelectorAll("[data-oz-cart-count], [data-cart-count], .cart-count-bubble span[aria-hidden='true'], .cart-dot")
      .forEach(function (element) {
        element.textContent = String(count);
        element.hidden = count < 1;
      });
  }

  function setButtonLabel(button, label, text) {
    if (label) label.textContent = text;
    else button.textContent = text;
  }

  function setError(element, message) {
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }

  function showToast(message) {
    const toast = getToast();
    const messageElement = toast.querySelector("[data-oz-product-card-toast-message]");

    messageElement.textContent = message;
    toast.classList.add("is-visible");

    window.clearTimeout(window.OZPlugProductCardToastTimer);
    window.OZPlugProductCardToastTimer = window.setTimeout(function () {
      toast.classList.remove("is-visible");
    }, 1800);
  }

  function getToast() {
    let toast = document.querySelector("[data-oz-product-card-toast]");

    if (!toast) {
      toast = document.createElement("div");
      toast.className = "oz-product-card-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      toast.setAttribute("aria-atomic", "true");
      toast.setAttribute("data-oz-product-card-toast", "");
      toast.innerHTML = '<span class="oz-product-card-toast__check" aria-hidden="true">✓</span><span data-oz-product-card-toast-message>Added to cart</span>';
      document.body.appendChild(toast);
    }

    return toast;
  }

  function ready(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  }

  function scheduleViewportVideoInit() {
    if (videoInitFrame) return;

    videoInitFrame = window.requestAnimationFrame(function () {
      videoInitFrame = null;
      initViewportVideos(document);
    });
  }

  function initViewportVideos(context) {
    if (!("IntersectionObserver" in window)) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (isConnectionConstrained()) return;
    if (!document.querySelector(VIDEO_SELECTOR)) return;

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(handleVideoIntersections, {
        rootMargin: VIDEO_ROOT_MARGIN,
        threshold: [0, VIDEO_STOP_THRESHOLD, VIDEO_FULL_THRESHOLD, 0.85, 1]
      });
    }

    observeVideoCards(context || document);
  }

  function observeVideoCards(root) {
    if (!root || !videoObserver) return 0;

    const videos = [];

    if (root.nodeType === 1 && root.matches && root.matches(VIDEO_SELECTOR)) {
      videos.push(root);
    }

    if ((root.nodeType === 1 || root.nodeType === 9) && root.querySelectorAll) {
      root.querySelectorAll(VIDEO_SELECTOR).forEach(function (video) {
        videos.push(video);
      });
    }

    let observedCount = 0;

    videos.forEach(function (video) {
      const card = video.closest(VIDEO_CARD_SELECTOR);
      if (!card || observedVideoCards.has(card)) return;
      observedVideoCards.add(card);
      videoObserver.observe(card);
      observedCount += 1;
    });

    return observedCount;
  }

  function handleVideoIntersections(entries) {
    entries.forEach(function (entry) {
      const card = entry.target;
      const video = card.querySelector(VIDEO_SELECTOR);
      if (!video) return;

      if (entry.intersectionRatio >= VIDEO_FULL_THRESHOLD && entry.isIntersecting) {
        scheduleCardVideoPlay(card, video);
        return;
      }

      clearCardVideoTimer(card);

      if (!entry.isIntersecting || entry.intersectionRatio <= VIDEO_STOP_THRESHOLD) {
        stopCardVideo(card, video);
      }
    });
  }

  function scheduleCardVideoPlay(card, video) {
    clearCardVideoTimer(card);

    const timer = window.setTimeout(function () {
      if (!isCardVisibleEnough(card, VIDEO_STOP_THRESHOLD)) return;
      playCardVideo(card, video);
    }, VIDEO_PLAY_DELAY);

    videoTimers.set(card, timer);
  }

  function clearCardVideoTimer(card) {
    const timer = videoTimers.get(card);
    if (timer) {
      window.clearTimeout(timer);
      videoTimers.delete(card);
    }
  }

  function isCardVisibleEnough(card, minimumRatio) {
    const rect = card.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const cardArea = Math.max(1, rect.width * rect.height);
    const visibleRatio = (visibleWidth * visibleHeight) / cardArea;

    return visibleRatio >= (minimumRatio || 0.12);
  }

  function isConnectionConstrained() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;

    return /(^|-)2g$/.test(connection.effectiveType || "");
  }

  function playCardVideo(card, video) {
    if (!video || !video.dataset.src || document.hidden) return;

    if (!video.dataset.loaded) {
      video.src = video.dataset.src;
      video.dataset.loaded = "true";
      video.load();
    }

    video.muted = true;
    video.playsInline = true;

    const playPromise = video.play();
    card.classList.add("is-video-active");

    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(function () {
        card.classList.remove("is-video-active");
      });
    }
  }

  function stopCardVideo(card, video) {
    clearCardVideoTimer(card);
    if (!video) return;

    try {
      video.pause();
      video.currentTime = 0;
    } catch (error) {
      // Ignore browser media reset errors.
    }

    card.classList.remove("is-video-active");
  }

  function pauseAllCardVideos() {
    document.querySelectorAll(VIDEO_SELECTOR).forEach(function (video) {
      const card = video.closest(VIDEO_CARD_SELECTOR);
      if (card) stopCardVideo(card, video);
    });
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
})();
